import { Link } from '@tanstack/react-router';
import { Cloud, CloudOff, RefreshCw, UserRound } from 'lucide-react';
import { useAccount } from '../account/store.ts';
import { useSync } from '../sync/service.ts';
import type { SyncBadge } from '../sync/types.ts';

const BADGE_TEXT: Record<SyncBadge, string | null> = {
  local: 'On this device only',
  synced: null,
  pending: 'Waiting to sync',
  uploading: 'Uploading photo…',
  notBackedUp: 'Not backed up (storage full)',
  requiresUpdate: 'Needs a newer app',
  deletedRemotely: 'Deleted on another device',
};

export function badgeText(b: SyncBadge): string | null {
  return BADGE_TEXT[b];
}

/** Account button with the sync state (header of the library). */
export function AccountButton() {
  const session = useAccount((s) => s.session);
  const status = useSync((s) => s.status);
  if (!session) {
    return (
      <Link
        to="/signin"
        className="t-label inline-flex h-9 items-center rounded-md bg-surface3 px-3 hover:bg-secondary-pressed"
      >
        Sign in
      </Link>
    );
  }
  const Icon = status === 'syncing' ? RefreshCw : status === 'offline' || status === 'error' ? CloudOff : Cloud;
  const label =
    status === 'syncing'
      ? 'Syncing'
      : status === 'offline'
        ? 'Offline'
        : status === 'error'
          ? 'Sync problem'
          : 'Synced';
  return (
    <Link
      to="/account"
      aria-label={`Account (${label})`}
      title={`Account · ${label}`}
      className="relative inline-flex size-11 items-center justify-center rounded-md hover:bg-white/8"
    >
      <UserRound size={20} strokeWidth={2.2} aria-hidden />
      <Icon
        size={12}
        strokeWidth={2.6}
        className={`absolute right-1.5 bottom-1.5 ${status === 'syncing' ? 'animate-spin text-accent' : status === 'idle' ? 'text-success' : 'text-dim'}`}
        aria-hidden
      />
    </Link>
  );
}
